import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, TextField, Typography } from '@mui/material';
import { RequestLifeCycle } from "app/appModels";
import { MprcButton, MprcFileSelection, MprcFormSubHeader, MprcRequestErrorDialog, MprcRequestErrorReport, MprcRequestIndicator } from "components";
import { UploadingStatus } from "components/FileUpload/AttachSelection";
import { IconButtonClose } from "components/iconButtons";
import { IconButtonExpandScreen } from "components/iconButtons/IconButtonExpandScreen";
import { AutoCompleteHierarchicalModel } from "components/MprcAutoCompleteHierarchical";
import { TopicAutoCompleteHierarchicalModel } from "components/MprcAutoCompleteHierarchical/AutoCompleteHierarchicalModel";
import { AsyncUploadResult, CreateBatchPublicationCommand, FileGetDto, GetPublicationAdvancedOptionsDto, GetPublicationChannelDto, PublicationCreationSettingsResponse, PublicationTagItem, TopicAutoCompleteHierarchicalModelDto } from "generated/CoreApi_v1";
import { useReferredState } from "hooks/useReferredState";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import {
    IInstitutionalBoxValidation,
    IPublicationChannelBoxValidation,
    PublicationAdvancedBox,
    PublicationChannelBox,
    PublicationTopicsBox
} from "views/knowledge/publication/creation/components";
import { PublicationDialogContent } from "views/knowledge/publication/creation/components/PublicationDialogContent";
import { IStickerBoxValidation } from "views/knowledge/publication/creation/components/PublicationStickerBox";

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    onClose: (result?: string) => void;
}

interface IValidation {
    editJustification?: boolean;
}

interface IBatchPublication {
    zipFile: FileGetDto | null;
    channel: GetPublicationChannelDto;
    topics: TopicAutoCompleteHierarchicalModelDto[];
    advancedOptions: GetPublicationAdvancedOptionsDto;
    editJustification: string | null;
}

interface IState {
    record: IBatchPublication;
    validation: IValidation & IInstitutionalBoxValidation & IPublicationChannelBoxValidation & IStickerBoxValidation;
    uploadingStatus: UploadingStatus;
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const CreateBatchPublication = (props: IProps) => {
    const { onClose } = props;
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();
    const api = useCoreApi();

    const config = useRef<PublicationCreationSettingsResponse | null>(null);
    const [contentTopics, setContentTopics] = useState<TopicAutoCompleteHierarchicalModel[]>([]);

    const [step, setStep] = useState<number>(1);
    const [lastError, setLastError] = useState<any>();
    const [formCycle, setFormCycle] = useState<RequestLifeCycle>(RequestLifeCycle.None);
    const [fullScreenMode, setFullScreenMode] = useState<boolean>(false);

    const steps = [];
    const StepFirstPage = 1;
    const StepSecondPage = 2;
    const StepThrirdPage = 3;

    const [state, setState, refState] = useReferredState<IState>(
        {
            record: {
                zipFile: null,
                topics: [],
                advancedOptions: {
                    isInstitutional: false,
                    viewPeriodStart: null,
                    viewPeriodEnd: null,
                    viewStickerUntil: null,
                    sticker: null,
                    tags: []
                },
                channel: null!,
                editJustification: null,
            },
            validation: {
                channel: undefined,
                period: true,
                viewStickerUntil: true,
                editJustification: undefined
            },

            uploadingStatus: "none",
        });


    ///
    const updateRecord = (data: Partial<IBatchPublication>) => {
        return { ...refState.current.record, ...data };
    };

    ///
    const setDefaultValues = () => {
        if (config.current && config.current.defaultChannel.length > 0) {
            const channel = config.current.defaultChannel[0];
            setState({
                ...state,
                record: updateRecord({ channel: { channelId: channel.key, name: channel.label, isPrivate: channel.isPrivate, requirePublicationEditJustification: false, thumbnailUrl: channel.thumbnailUrl! } }),
                validation: { ...state.validation, channel: true }
            });
        }
        if (config.current && config.current.topics.length > 0) {
            setState({
                ...state,
                record: updateRecord({ topics: config.current.topics }),
            });
        }
    };

    ///
    const handleUpdateAttach = (uploadResult: AsyncUploadResult) => {
        setState({
            ...state,
            record: updateRecord({ zipFile: uploadResult.mainFile }),
        });
    };

    ///
    const handleUploadingStatus = (status: UploadingStatus) => {
        setState({ ...refState.current, uploadingStatus: status });
    };

    ///
    const loadSettings = async () => {
        setFormCycle(RequestLifeCycle.Loading);

        api.v1.knowledgePublication.getCreationSettings({ defaultChannelId: null, defaultTopicId: null, exclusiveListId: null })
            .then((result) => {
                config.current = result.data;
                setDefaultValues();
                setFormCycle(RequestLifeCycle.None);
            })
            .catch((error: any) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.LoadFailed);
            });
    };

    ///
    const save = () => {

        setFormCycle(RequestLifeCycle.Posting);

        const { record } = state;

        const dto: CreateBatchPublicationCommand = {
            zipFileId: record.zipFile!.fileId,
            topics: record.topics.map((topic) => topic.key),
            advancedOptions: {
                isInstitutional: record.advancedOptions.isInstitutional,
                stickerId: record.advancedOptions.sticker?.stickerId ?? null,
                viewStickerUntil: record.advancedOptions.viewStickerUntil,
                viewPeriodEnd: record.advancedOptions.viewPeriodEnd,
                viewPeriodStart: record.advancedOptions.viewPeriodStart,
                tags: record.advancedOptions.tags.map((tag) => ({ tagId: tag.tagId, tagName: tag.tagName } as PublicationTagItem))
            },
            channelId: record.channel?.channelId,
            editJustification: record.editJustification
        };

        api.v1.knowledgePublication.createBatchPublication(dto)
            .then((response) => {
                onClose(response.data);
                setFormCycle(RequestLifeCycle.None);
                if (response.data) {
                    enqueueSnackbar(t("Publication.BatchPublication.SendMessage"), { variant: "success" });
                } else {
                    enqueueSnackbar(t("shared:Record.FailSuccessMessage"), { variant: "error" });
                }
            })
            .catch((error) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.PostFailed);
            });
    };

    ///
    const goStep2 = () => {
        const currentTopics = refState.current.record.topics;

        const topics = currentTopics as TopicAutoCompleteHierarchicalModel[];

        topics.filter(p => p.selectable).forEach((topic) => {
            if (currentTopics.find(p => p.key == topic.key) == null && currentTopics.length < 5) {
                currentTopics.push(topic as TopicAutoCompleteHierarchicalModelDto);
                topic.selectable = false;
            }
        });

        setContentTopics(topics);
        updateRecord({ topics: currentTopics });

        setStep(2);
    };

    ///
    const canGoStep2 = () => {
        const { uploadingStatus, record: { zipFile } } = state;
        return (uploadingStatus != "uploading" && zipFile != null);
    };

    ///
    const goStep3 = () => {
        setStep(3);
    };

    ///
    const canGoStep3 = () => {
        const { validation } = state;
        return (validation.channel != null);
    };

    ///
    const canSave = (): boolean => {
        const { validation, record } = state;
        return canGoStep2()
            && validation.channel === true
            && validation.period === true
            && (validation.viewStickerUntil == undefined || validation.viewStickerUntil == true)
            && (!record.channel.requirePublicationEditJustification || validation.editJustification === true);
    };

    ///
    const getContinueButton = () => {
        return (
            <MprcButton
                variant="contained"
                color="primary"
                disabled={!canGoStep2()}
                onClick={goStep2}
            >
                <span>{t("Publication.Common.Form.ButtonContinueText")}</span>
            </MprcButton>
        );
    };

    ///////////////////////////////////////////////////////////////////////
    /// Passos do Formulário
    ///////////////////////////////////////////////////////////////////////

    ///
    steps[StepFirstPage] = () => {
        const { record } = state;

        return (
            <>
                <PublicationDialogContent>
                    <Stack direction="column" marginY={8} spacing={2}>
                        <Typography variant="h2" width={"100%"} component="h2" marginBottom={1} textAlign={"center"}>
                            <span>{t("Publication.BatchPublication.Subtitle")}</span>
                        </Typography>
                        <Box marginTop={2}>
                            <MprcFileSelection
                                uploadMessage={[t("Publication.BatchPublication.UploadMessage")]}
                                acceptedFiles={{ "": [".zip"] }}
                                selectedFile={record.zipFile}
                                onFileUploadStarted={() => handleUploadingStatus("uploading")}
                                onFileUploadError={() => updateRecord({ zipFile: null })}
                                onFileUploadFinished={() => handleUploadingStatus("none")}
                                onFileUploadSuccess={handleUpdateAttach}
                                onClear={() => updateRecord({ zipFile: null })}
                            />
                            <Typography variant="body2" component="p" marginTop={1}>
                                <span>{t("Publication.BatchPublication.DocumentTypeHint")}</span>
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="body2" component="p">
                                <span>{t("Publication.BatchPublication.FileMessage_L1")}</span>
                            </Typography>
                            <Typography variant="body2" component="p">
                                <span>- {t("Publication.BatchPublication.FileMessage_L2")}</span>
                            </Typography>
                            <Typography variant="body2" component="p">
                                <span>- {t("Publication.BatchPublication.FileMessage_L3")}</span>
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="body2" component="p">
                                <span>{t("Publication.BatchPublication.FinalProcessMessage_L1")}</span>
                            </Typography>
                            <Typography variant="body2" component="p">
                                <span>{t("Publication.BatchPublication.FinalProcessMessage_L2")}</span>
                            </Typography>
                        </Box>
                    </Stack>
                </PublicationDialogContent>

                <DialogActions>
                    <Grid container justifyContent="space-between" alignItems="center" width="100%">
                        <Grid>
                            <b>{t("Publication.BatchPublication.Footer1")}</b>
                            <span>&nbsp;&raquo;&nbsp;</span>
                            <span>{t("Publication.BatchPublication.Footer2")}</span>
                        </Grid>
                        <Grid>
                            {
                                getContinueButton()
                            }
                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    };

    ///
    steps[StepSecondPage] = () => {
        const { record, validation } = state;
        return (
            <>
                <DialogContent>

                    <PublicationChannelBox
                        channel={record.channel}
                        suggestions={config.current!.channelSuggestions}
                        onUpdateState={
                            ({ channel, validation }) => setState({
                                ...state,
                                record: updateRecord({ channel: channel! }),
                                validation: { ...state.validation, ...validation }
                            })
                        }
                        validation={validation}
                    />

                    <PublicationTopicsBox
                        channelId={record.channel?.channelId}
                        topics={record.topics as AutoCompleteHierarchicalModel[]}
                        suggestions={contentTopics}
                        publication={{ title: null, content: null }}
                        onUpdateState={
                            (topics) => setState({
                                ...state,
                                record: updateRecord({ topics: topics as TopicAutoCompleteHierarchicalModelDto[] })
                            })
                        }
                    />

                    <PublicationAdvancedBox
                        channelId={record.channel?.channelId}
                        allowInstitutionalPublisher={config.current?.allowInstitutionalPublisher ?? false}
                        canCreateTags={config.current?.canCreateTags ?? false}
                        onUpdateState={({ advancedOptions, validation }) => {
                            setState({
                                ...state,
                                record: updateRecord({ advancedOptions: { ...advancedOptions } }),
                                validation: { ...state.validation, ...validation }
                            });
                        }}
                        advancedOptions={{ ...record.advancedOptions }}
                        validation={validation}
                    />
                </DialogContent>

                <DialogActions>
                    <Grid container justifyContent="space-between" alignItems="center" width="100%">
                        <Grid>
                            <span>{t("Publication.BatchPublication.Footer1")}</span>
                            <span>&nbsp;&raquo;&nbsp;</span>
                            <b>{t("Publication.BatchPublication.Footer2")}</b>
                        </Grid>
                        <Grid>
                            <Grid container alignItems="center" spacing={2}>


                                <Grid>
                                    <MprcButton
                                        variant="contained"
                                        onClick={() => setStep(1)}
                                        sx={{ mr: 1 }}
                                    >
                                        <span>{t("Publication.Common.Form.ButtonBackText")}</span>
                                    </MprcButton>

                                    {
                                        record.channel?.requirePublicationEditJustification == true
                                            ? <MprcButton
                                                variant="contained"
                                                color="primary"
                                                disabled={!canGoStep3()}
                                                onClick={goStep3}
                                            >
                                                <span>{t("Publication.Common.Form.ButtonContinueText")}</span>
                                            </MprcButton>
                                            : <MprcButton
                                                variant="contained"
                                                color="success"
                                                onClick={() => save()}
                                                disabled={!canSave()}
                                            >
                                                <span>{t("Publication.Common.Form.ButtonFinishText")}</span>
                                            </MprcButton>
                                    }

                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    };


    steps[StepThrirdPage] = () => {
        const { record, validation } = state;
        return (
            <>
                <DialogContent>
                    <MprcFormSubHeader
                        title={t("Publication.CreatePost.EditJustificationTitle") + " *"}
                        subtitle={t("Publication.CreatePost.EditJustificationSubtitle")}
                        margin="dense"
                    />
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        name="editJustification"
                        margin="dense"
                        variant="outlined"
                        rows={5}
                        placeholder={t("Publication.CreatePost.EditJustificationHint")}
                        required
                        slotProps={{
                            input: {
                                inputProps: { maxLength: 2048 }
                            }
                        }}
                        value={record.editJustification ?? ""}
                        error={validation.editJustification == false}
                        onChange={(e) =>
                            setState({
                                ...refState.current,
                                record: updateRecord({ editJustification: e.target.value }),
                                validation: { ...refState.current.validation, editJustification: e.target.value.length > 0 }
                            })
                        }
                    />
                </DialogContent>

                <DialogActions>
                    <Grid container justifyContent="space-between" alignItems="end">
                        <Grid>
                            <MprcButton
                                variant="contained"
                                onClick={goStep2}
                                sx={{ mr: 1 }}
                            >
                                <span>{t("Publication.Common.Form.ButtonBackText")}</span>
                            </MprcButton>

                            <MprcButton
                                variant="contained"
                                color="success"
                                onClick={() => save()}
                                disabled={!canSave()}
                            >
                                <span>{t("Publication.Common.Form.ButtonFinishText")}</span>
                            </MprcButton>
                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    };



    ///////////////////////////////////////////////////////////////////////
    /// Effect's
    ///////////////////////////////////////////////////////////////////////

    // Ao carregar o dialog
    useEffect(() => {
        loadSettings();
    }, []);

    ///////////////////////////////////////////////////////////////////////
    /// Render
    ///////////////////////////////////////////////////////////////////////
    return (
        <Dialog
            open={true}
            maxWidth={"md"}
            fullWidth
            fullScreen={fullScreenMode}
            sx={fullScreenMode ? { "& .MuiPaper-root": { maxHeight: "90%", maxWidth: "90%", minHeight: "500px" } } : { minHeight: "500px" }}
        >
            <DialogTitle>
                <Grid container justifyContent="space-between">
                    <Grid>
                        <Typography variant="h4">
                            {t("Publication.BatchPublication.Title")}
                        </Typography>
                    </Grid>
                    <Grid>
                        <IconButtonExpandScreen
                            expanded={fullScreenMode}
                            size="small"
                            onClick={() => setFullScreenMode(!fullScreenMode)}
                        />
                        <IconButtonClose
                            color="inherit"
                            size="small"
                            onClick={() => onClose()}
                        />
                    </Grid>
                </Grid>
            </DialogTitle>

            <MprcRequestIndicator type="request" visible={formCycle == RequestLifeCycle.Loading} />

            <MprcRequestIndicator type="post" visible={formCycle == RequestLifeCycle.Posting} />

            {
                lastError && formCycle == RequestLifeCycle.LoadFailed
                    ? <MprcRequestErrorReport error={lastError} />
                    : (
                        <>
                            <MprcRequestErrorDialog error={lastError} onClose={() => setLastError(null)} />
                            {steps[step]()}
                        </>
                    )
            }

        </Dialog>
    );
};
